import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { 
  Building2, DoorOpen, Bed, Users, Plus, UserPlus,
  DollarSign, ClipboardList, UserMinus, Check, X, Utensils, Calendar, LogOut, Settings, Save, Pencil, Trash2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Student Hostel View Component
const StudentHostelView = ({ getAuthHeader }) => {
  const [myHostel, setMyHostel] = useState(null);
  const [messMenu, setMessMenu] = useState([]);
  const [messSettings, setMessSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyHostelData();
  }, []);

  const fetchMyHostelData = async () => {
    try {
      // First try to get student's room allocation with mess menu
      const myRoomRes = await axios.get(`${API_URL}/api/hostel/my-room`, { headers: getAuthHeader() });
      const roomData = myRoomRes.data;
      
      if (roomData.allocated) {
        setMyHostel(roomData);
        setMessMenu(roomData.mess_menu || []);
        setMessSettings(roomData.mess_settings);
      } else {
        // Fallback to general mess menu
        try {
          const menuRes = await axios.get(`${API_URL}/api/hostel/mess/menu`, { headers: getAuthHeader() });
          setMessMenu(menuRes.data || []);
        } catch (e) {
          // Ignore menu fetch error
        }
        setMyHostel(null);
      }
    } catch (error) {
      console.error('Failed to fetch hostel data:', error);
      // Try to at least get mess menu
      try {
        const menuRes = await axios.get(`${API_URL}/api/hostel/mess/menu`, { headers: getAuthHeader() });
        setMessMenu(menuRes.data || []);
      } catch (e) {
        // Ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  
  // Group menu by day and meal type
  const menuByDay = {};
  messMenu.forEach(m => {
    const day = m.day?.toLowerCase();
    if (!day) return;
    if (!menuByDay[day]) {
      menuByDay[day] = { day };
    }
    // Handle both formats: meal_type based and direct properties
    if (m.meal_type) {
      menuByDay[day][m.meal_type] = m.items;
    }
    // Also handle if breakfast/lunch/snacks/dinner are direct properties
    ['breakfast', 'lunch', 'snacks', 'dinner'].forEach(meal => {
      if (m[meal]) {
        menuByDay[day][meal] = m[meal];
      }
    });
  });
  
  const todayMenu = menuByDay[today];

  if (loading) {
    return (
      <DashboardLayout title="My Hostel">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Hostel">
      <div className="space-y-6">
        {/* Room Details */}
        {myHostel ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                My Room Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-muted-foreground">Building</Label>
                  <p className="font-semibold">{myHostel.building_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Room</Label>
                  <p className="font-semibold">{myHostel.room_number || 'N/A'}</p>
                  {myHostel.room_type && <p className="text-xs text-muted-foreground">{myHostel.room_type}</p>}
                </div>
                <div>
                  <Label className="text-muted-foreground">Bed</Label>
                  <p className="font-semibold">{myHostel.bed_number || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Monthly Fee</Label>
                  <p className="font-semibold text-primary">₹{(myHostel.monthly_fee || 0).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Deposit</Label>
                  <p className="font-semibold">₹{(myHostel.deposit_amount || 0).toLocaleString()}</p>
                </div>
              </div>
              {myHostel.warden_name && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm"><strong>Warden:</strong> {myHostel.warden_name} | <strong>Contact:</strong> {myHostel.warden_phone || 'N/A'}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No hostel allocation found</p>
            </CardContent>
          </Card>
        )}

        {/* Today's Menu */}
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Utensils className="h-5 w-5" />
              Today Menu ({today.charAt(0).toUpperCase() + today.slice(1)})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayMenu ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Breakfast</p>
                  <p className="font-medium">{todayMenu.breakfast || 'Not set'}</p>
                  {messSettings?.breakfast_time && <p className="text-xs text-muted-foreground mt-1">{messSettings.breakfast_time}</p>}
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Lunch</p>
                  <p className="font-medium">{todayMenu.lunch || 'Not set'}</p>
                  {messSettings?.lunch_time && <p className="text-xs text-muted-foreground mt-1">{messSettings.lunch_time}</p>}
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Snacks</p>
                  <p className="font-medium">{todayMenu.snacks || 'Not set'}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Dinner</p>
                  <p className="font-medium">{todayMenu.dinner || 'Not set'}</p>
                  {messSettings?.dinner_time && <p className="text-xs text-muted-foreground mt-1">{messSettings.dinner_time}</p>}
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">Menu not set for today</p>
            )}
          </CardContent>
        </Card>

        {/* Weekly Menu */}
        <Card>
          <CardHeader>
            <CardTitle>Weekly Mess Menu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {DAYS.map(day => {
                const menu = menuByDay[day];
                return (
                  <div key={day} className={`p-3 border rounded-lg ${day === today ? 'border-primary bg-primary/5' : ''}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                      {day === today && <Badge className="bg-primary">Today</Badge>}
                    </div>
                    {menu ? (
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <span><strong>B:</strong> {menu.breakfast || '-'}</span>
                        <span><strong>L:</strong> {menu.lunch || '-'}</span>
                        <span><strong>S:</strong> {menu.snacks || '-'}</span>
                        <span><strong>D:</strong> {menu.dinner || '-'}</span>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Menu not set</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Leave & Gate Pass Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Leave & Gate Pass
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6 space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-muted-foreground mb-3">
                  To request hostel leave or gate pass, please use the unified Gate Pass system. 
                  Your parent will be notified and must approve before the warden can process your request.
                </p>
                <a href="/gate-pass" className="inline-block">
                  <Button data-testid="go-to-gate-pass-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Go to My Gate Pass
                  </Button>
                </a>
              </div>
              <p className="text-xs text-muted-foreground">
                Select Home Visit for hostel leave requests
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const HostelPageV2Admin = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({});
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [messMenu, setMessMenu] = useState([]);
  const [messSettings, setMessSettings] = useState({});
  const [hostelSettings, setHostelSettings] = useState({});
  const [students, setStudents] = useState([]);
  const [staffList, setStaffList] = useState([]);  // For Warden selection
  
  // Dialogs
  const [buildingDialog, setBuildingDialog] = useState(false);
  const [roomDialog, setRoomDialog] = useState(false);
  const [allocateDialog, setAllocateDialog] = useState(false);
  const [visitorDialog, setVisitorDialog] = useState(false);
  const [menuDialog, setMenuDialog] = useState(false);
  const [messSettingsDialog, setMessSettingsDialog] = useState(false);
  const [hostelSettingsDialog, setHostelSettingsDialog] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  
  // Edit mode states
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingMenu, setEditingMenu] = useState(null);
  
  // Forms
  const [buildingForm, setBuildingForm] = useState({ name: '', building_type: 'boys', total_floors: 1, warden_id: '', warden_name: '' });
  const [roomForm, setRoomForm] = useState({ room_number: '', floor: 1, room_type: 'double', total_beds: 2, monthly_fee: 0 });
  const [allocateForm, setAllocateForm] = useState({ student_id: '', room_id: '', bed_number: 1, start_date: '', mess_type: 'veg' });
  const [visitorForm, setVisitorForm] = useState({ student_id: '', visitor_name: '', visitor_phone: '', relation: '', purpose: '' });
  const [menuForm, setMenuForm] = useState({ day: 'Monday', meal_type: 'breakfast', items: '' });
  const [settingsForm, setSettingsForm] = useState({});
  const [hostelSettingsForm, setHostelSettingsForm] = useState({ security_deposit_months: 2, late_return_fine: 100, visitor_hours: '10:00 - 18:00', curfew_time: '22:00' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, bldgRes, roomRes, allocRes, leaveRes, visRes, menuRes, messRes, hostelSettingsRes, studRes, staffRes] = await Promise.all([
        axios.get(`${API_URL}/api/hostel/dashboard`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/hostel/buildings`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hostel/rooms`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hostel/allocations`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hostel/leave-requests`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hostel/visitors`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hostel/mess/menu`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hostel/mess/settings`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/hostel/settings`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setDashboard(dashRes.data || {});
      setBuildings(bldgRes.data || []);
      setRooms(roomRes.data || []);
      setAllocations(allocRes.data || []);
      setLeaveRequests(leaveRes.data || []);
      setVisitors(visRes.data || []);
      // messMenu must be array for .find() to work - use menuRes not messRes
      setMessMenu(Array.isArray(menuRes.data) ? menuRes.data : []);
      setMessSettings(messRes.data || {});
      setSettingsForm(messRes.data || {});
      // Hostel settings
      const hSettings = hostelSettingsRes.data || { security_deposit_months: 2, late_return_fine: 100, visitor_hours: '10:00 - 18:00', curfew_time: '22:00' };
      setHostelSettings(hSettings);
      setHostelSettingsForm(hSettings);
      // Handle paginated students response
      const studentsData = studRes.data?.items || studRes.data || [];
      setStudents(Array.isArray(studentsData) ? studentsData : []);
      // Staff for Warden selection
      const staffData = staffRes.data?.items || staffRes.data || [];
      setStaffList(Array.isArray(staffData) ? staffData.filter(s => s.status === 'active' || !s.status) : []);
    } catch (error) {
      console.error('Hostel fetch error:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // ========== BUILDING CRUD ==========
  const createBuilding = async () => {
    try {
      await axios.post(`${API_URL}/api/hostel/buildings`, null, {
        headers: getAuthHeader(),
        params: buildingForm
      });
      toast.success('Building created');
      closeBuildingDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to create building');
    }
  };

  const updateBuilding = async () => {
    try {
      await axios.put(`${API_URL}/api/hostel/buildings/${editingBuilding}`, null, {
        headers: getAuthHeader(),
        params: buildingForm
      });
      toast.success('Building updated');
      closeBuildingDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update building');
    }
  };

  const deleteBuilding = async (id, hasRooms) => {
    if (hasRooms) {
      toast.error('Cannot delete building with rooms. Delete all rooms first.');
      return;
    }
    if (!window.confirm('Delete this building?')) return;
    try {
      await axios.delete(`${API_URL}/api/hostel/buildings/${id}`, { headers: getAuthHeader() });
      toast.success('Building deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete building');
    }
  };

  const openEditBuilding = (b) => {
    setEditingBuilding(b.id);
    setBuildingForm({
      name: b.name || '',
      building_type: b.building_type || 'boys',
      total_floors: b.total_floors || 1,
      warden_id: b.warden_id || '',
      warden_name: b.warden_name || ''
    });
    setBuildingDialog(true);
  };

  const closeBuildingDialog = () => {
    setBuildingDialog(false);
    setEditingBuilding(null);
    setBuildingForm({ name: '', building_type: 'boys', total_floors: 1, warden_id: '', warden_name: '' });
  };

  // ========== ROOM CRUD ==========
  const createRoom = async () => {
    if (!selectedBuilding) return;
    try {
      await axios.post(`${API_URL}/api/hostel/rooms`, null, {
        headers: getAuthHeader(),
        params: { ...roomForm, building_id: selectedBuilding }
      });
      toast.success('Room created');
      closeRoomDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to create room');
    }
  };

  const updateRoom = async () => {
    try {
      await axios.put(`${API_URL}/api/hostel/rooms/${editingRoom}`, null, {
        headers: getAuthHeader(),
        params: roomForm
      });
      toast.success('Room updated');
      closeRoomDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update room');
    }
  };

  const deleteRoom = async (id, hasStudents) => {
    if (hasStudents) {
      toast.error('Cannot delete room with allocated students. Remove all students first.');
      return;
    }
    if (!window.confirm('Delete this room?')) return;
    try {
      await axios.delete(`${API_URL}/api/hostel/rooms/${id}`, { headers: getAuthHeader() });
      toast.success('Room deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    }
  };

  const openEditRoom = (r) => {
    setEditingRoom(r.id);
    setSelectedBuilding(r.building_id);
    setRoomForm({
      room_number: r.room_number || '',
      floor: r.floor || 1,
      room_type: r.room_type || 'double',
      total_beds: r.total_beds || 2,
      monthly_fee: r.monthly_fee || r.monthly_rent || 0
    });
    setRoomDialog(true);
  };

  const closeRoomDialog = () => {
    setRoomDialog(false);
    setEditingRoom(null);
    setSelectedBuilding(null);
    setRoomForm({ room_number: '', floor: 1, room_type: 'double', total_beds: 2, monthly_fee: 0 });
  };

  // ========== STUDENT ALLOCATION ==========
  const allocateStudent = async () => {
    try {
      await axios.post(`${API_URL}/api/hostel/allocate`, null, {
        headers: getAuthHeader(),
        params: allocateForm
      });
      toast.success('Student allocated to hostel');
      setAllocateDialog(false);
      setAllocateForm({ student_id: '', room_id: '', bed_number: 1, start_date: '', mess_type: 'veg' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate');
    }
  };

  const deallocateStudent = async (allocationId) => {
    if (!window.confirm('Remove student from hostel?')) return;
    try {
      await axios.post(`${API_URL}/api/hostel/deallocate/${allocationId}`, null, { headers: getAuthHeader() });
      toast.success('Student removed from hostel');
      fetchData();
    } catch (error) {
      toast.error('Failed to deallocate');
    }
  };

  const handleLeaveRequest = async (requestId, approved) => {
    try {
      await axios.put(`${API_URL}/api/hostel/leave-requests/${requestId}/approve`, null, {
        headers: getAuthHeader(),
        params: { approved }
      });
      toast.success(`Leave request ${approved ? 'approved' : 'rejected'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update leave request');
    }
  };

  const registerVisitor = async () => {
    try {
      await axios.post(`${API_URL}/api/hostel/visitors`, null, {
        headers: getAuthHeader(),
        params: visitorForm
      });
      toast.success('Visitor registered');
      setVisitorDialog(false);
      setVisitorForm({ student_id: '', visitor_name: '', visitor_phone: '', relation: '', purpose: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to register visitor');
    }
  };

  const checkoutVisitor = async (visitorId) => {
    try {
      await axios.put(`${API_URL}/api/hostel/visitors/${visitorId}/checkout`, null, { headers: getAuthHeader() });
      toast.success('Visitor checked out');
      fetchData();
    } catch (error) {
      toast.error('Failed to checkout');
    }
  };

  const saveMessMenu = async () => {
    try {
      await axios.post(`${API_URL}/api/hostel/mess/menu`, null, {
        headers: getAuthHeader(),
        params: menuForm
      });
      toast.success('Menu saved');
      closeMenuDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to save menu');
    }
  };

  const deleteMessMenu = async (day) => {
    if (!window.confirm(`Delete menu for ${day}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/hostel/mess/menu/${day}`, { headers: getAuthHeader() });
      toast.success('Menu deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete menu');
    }
  };

  const openEditMenu = (menu) => {
    setEditingMenu(menu.day);
    setMenuForm({
      day: menu.day,
      meal_type: 'breakfast',
      items: menu.breakfast || ''
    });
    setMenuDialog(true);
  };

  const closeMenuDialog = () => {
    setMenuDialog(false);
    setEditingMenu(null);
    setMenuForm({ day: 'Monday', meal_type: 'breakfast', items: '' });
  };

  const saveMessSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/hostel/mess/settings`, null, {
        headers: getAuthHeader(),
        params: settingsForm
      });
      toast.success('Settings saved');
      setMessSettingsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const saveHostelSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/hostel/settings`, null, {
        headers: getAuthHeader(),
        params: hostelSettingsForm
      });
      toast.success('Hostel settings saved');
      setHostelSettingsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save hostel settings');
    }
  };

  const generateMonthlyFees = async () => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      const res = await axios.post(`${API_URL}/api/hostel/fees/generate-monthly`, null, {
        headers: getAuthHeader(),
        params: { month }
      });
      toast.success(`Generated ${res.data.generated} fees, skipped ${res.data.skipped}`);
    } catch (error) {
      toast.error('Failed to generate fees');
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snacks'];

  const stats = [
    { title: 'Buildings', value: dashboard.total_buildings || 0, icon: Building2, color: 'bg-blue-500' },
    { title: 'Rooms', value: dashboard.total_rooms || 0, icon: DoorOpen, color: 'bg-purple-500' },
    { title: 'Total Beds', value: dashboard.total_beds || 0, icon: Bed, color: 'bg-green-500' },
    { title: 'Hostellers', value: dashboard.total_hostellers || 0, icon: Users, color: 'bg-amber-500' },
    { title: 'Available', value: dashboard.available_beds || 0, icon: Bed, color: 'bg-emerald-500' },
    { title: 'Occupancy', value: `${dashboard.occupancy_rate || 0}%`, icon: ClipboardList, color: 'bg-indigo-500' },
  ];

  return (
    <DashboardLayout title="Hostel Management">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-8 w-full max-w-5xl">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="buildings">Buildings</TabsTrigger>
          <TabsTrigger value="rooms">Rooms</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="mess">Mess</TabsTrigger>
          <TabsTrigger value="leaves">Leaves</TabsTrigger>
          <TabsTrigger value="visitors">Visitors</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-xs font-medium text-muted-foreground">{stat.title}</CardTitle>
                      <div className={`${stat.color} p-2 rounded-lg`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold">{stat.value}</div></CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Occupancy by Building</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {buildings.map(b => (
                  <div key={b.id} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{b.name}</span>
                      <span>{b.occupied_beds}/{b.total_beds} beds</span>
                    </div>
                    <Progress value={(b.occupied_beds / Math.max(b.total_beds, 1)) * 100} />
                  </div>
                ))}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle className="text-base">Fee Collection (This Month)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Total:</span><span className="font-bold">₹{dashboard.fee_total || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Collected:</span><span className="font-bold text-green-600">₹{dashboard.fee_collected || 0}</span></div>
                <Button size="sm" className="w-full mt-2" variant="outline" onClick={generateMonthlyFees}>
                  <DollarSign className="h-4 w-4 mr-2" />Generate Monthly Fees
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Mess Distribution</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Veg:</span><span className="font-bold">{dashboard.mess_stats?.veg || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Non-Veg:</span><span className="font-bold">{dashboard.mess_stats?.non_veg || 0}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">No Mess:</span><span className="font-bold">{dashboard.mess_stats?.no_mess || 0}</span></div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Pending Leave Requests ({leaveRequests.filter(l => l.status === 'pending').length})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {leaveRequests.filter(l => l.status === 'pending').length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No pending requests</p>
              ) : (
                <div className="space-y-2">
                  {leaveRequests.filter(l => l.status === 'pending').slice(0, 5).map(leave => (
                    <div key={leave.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{leave.student_name}</p>
                        <p className="text-sm text-muted-foreground">{leave.start_date} to {leave.end_date} • {leave.reason}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleLeaveRequest(leave.id, true)}><Check className="h-4 w-4 text-green-500" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleLeaveRequest(leave.id, false)}><X className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Buildings Tab */}
        <TabsContent value="buildings" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Hostel Buildings</h3>
            <Button onClick={() => setBuildingDialog(true)} data-testid="add-building-btn"><Plus className="h-4 w-4 mr-2" />Add Building</Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {buildings.map(building => {
              const roomCount = rooms.filter(r => r.building_id === building.id).length;
              return (
              <Card key={building.id} data-testid={`building-${building.name}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{building.name}</CardTitle>
                    <Badge variant={building.building_type === 'boys' ? 'default' : 'secondary'}>{building.building_type}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Floors:</span> {building.total_floors}</div>
                    <div><span className="text-muted-foreground">Rooms:</span> {building.total_rooms || roomCount}</div>
                    <div><span className="text-muted-foreground">Beds:</span> {building.total_beds}</div>
                    <div><span className="text-muted-foreground">Occupied:</span> {building.occupied_beds}</div>
                  </div>
                  {building.warden_name && <p className="text-sm"><span className="text-muted-foreground">Warden:</span> {building.warden_name}</p>}
                  <div className="flex gap-2 pt-2 border-t">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => { setSelectedBuilding(building.id); setRoomDialog(true); }}>
                      <Plus className="h-3 w-3 mr-1" />Room
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditBuilding(building)}><Pencil className="h-3 w-3" /></Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteBuilding(building.id, roomCount > 0)} disabled={roomCount > 0}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            )})}
          </div>
        </TabsContent>

        {/* Rooms Tab */}
        <TabsContent value="rooms" className="space-y-4">
          <h3 className="text-lg font-semibold">Rooms ({rooms.length})</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {rooms.map(room => {
              const occupiedCount = Array.isArray(room.occupied_beds) ? room.occupied_beds.length : (room.occupied_beds || 0);
              const hasStudents = occupiedCount > 0;
              return (
              <Card key={room.id} className={room.available_beds === 0 ? 'border-red-200' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Room {room.room_number}</CardTitle>
                    <Badge variant={room.available_beds > 0 ? 'outline' : 'destructive'}>{room.available_beds > 0 ? `${room.available_beds} free` : 'Full'}</Badge>
                  </div>
                  <CardDescription>{room.building_name} • Floor {room.floor}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><span className="capitalize">{room.room_type}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Beds:</span><span>{occupiedCount}/{room.total_beds}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Fee:</span><span>₹{room.monthly_fee || room.monthly_rent}/month</span></div>
                  <div className="flex gap-2 pt-2 border-t">
                    <Button variant="outline" size="sm" onClick={() => openEditRoom(room)}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteRoom(room.id, hasStudents)} disabled={hasStudents}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                  </div>
                </CardContent>
              </Card>
            )})}
          </div>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Hostellers ({allocations.length})</h3>
            <Button onClick={() => setAllocateDialog(true)} data-testid="allocate-student-btn"><UserPlus className="h-4 w-4 mr-2" />Allocate Student</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Student</th>
                    <th className="p-3 text-left text-sm font-medium">Room</th>
                    <th className="p-3 text-left text-sm font-medium">Building</th>
                    <th className="p-3 text-left text-sm font-medium">Mess Type</th>
                    <th className="p-3 text-left text-sm font-medium">Monthly Fee</th>
                    <th className="p-3 text-left text-sm font-medium">Since</th>
                    <th className="p-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map(alloc => (
                    <tr key={alloc.id} className="border-t">
                      <td className="p-3">
                        <div><p className="font-medium">{alloc.student_name}</p><p className="text-xs text-muted-foreground">{alloc.student_roll}</p></div>
                      </td>
                      <td className="p-3">{alloc.room_number} (Bed {alloc.bed_number})</td>
                      <td className="p-3">{alloc.building_name}</td>
                      <td className="p-3 capitalize">{alloc.mess_type?.replace('_', ' ')}</td>
                      <td className="p-3">₹{alloc.total_monthly_fee || (alloc.monthly_fee + alloc.mess_fee)}</td>
                      <td className="p-3 text-sm">{alloc.start_date}</td>
                      <td className="p-3">
                        <Button size="sm" variant="destructive" onClick={() => deallocateStudent(alloc.id)}><UserMinus className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mess Tab */}
        <TabsContent value="mess" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Mess Management</h3>
            <div className="flex gap-2">
              <Button onClick={() => setMenuDialog(true)} data-testid="add-menu-btn"><Utensils className="h-4 w-4 mr-2" />Add Menu</Button>
            </div>
          </div>

          <div className="grid md:grid-cols-1 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Meal Timings</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-muted-foreground text-xs">Breakfast</p>
                  <p className="font-semibold">{messSettings.breakfast_time || 'Not set'}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-muted-foreground text-xs">Lunch</p>
                  <p className="font-semibold">{messSettings.lunch_time || 'Not set'}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-muted-foreground text-xs">Snacks</p>
                  <p className="font-semibold">{messSettings.snacks_time || 'Not set'}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-muted-foreground text-xs">Dinner</p>
                  <p className="font-semibold">{messSettings.dinner_time || 'Not set'}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Weekly Menu</CardTitle></CardHeader>
            <CardContent>
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left text-sm font-medium">Day</th>
                    <th className="p-2 text-left text-sm font-medium">Breakfast</th>
                    <th className="p-2 text-left text-sm font-medium">Lunch</th>
                    <th className="p-2 text-left text-sm font-medium">Snacks</th>
                    <th className="p-2 text-left text-sm font-medium">Dinner</th>
                    <th className="p-2 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map(day => {
                    const dayMenu = messMenu.filter(m => m.day === day);
                    const hasMenu = dayMenu.length > 0;
                    return (
                    <tr key={day} className="border-t">
                      <td className="p-2 font-medium">{day}</td>
                      <td className="p-2 text-sm">{dayMenu.find(m => m.meal_type === 'breakfast')?.items || '-'}</td>
                      <td className="p-2 text-sm">{dayMenu.find(m => m.meal_type === 'lunch')?.items || '-'}</td>
                      <td className="p-2 text-sm">{dayMenu.find(m => m.meal_type === 'snacks')?.items || '-'}</td>
                      <td className="p-2 text-sm">{dayMenu.find(m => m.meal_type === 'dinner')?.items || '-'}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setMenuForm({ day, meal_type: 'breakfast', items: '' }); setMenuDialog(true); }}><Pencil className="h-3 w-3" /></Button>
                          {hasMenu && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteMessMenu(day)}><Trash2 className="h-3 w-3" /></Button>}
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaves Tab - Redirects to Gate Pass System */}
        <TabsContent value="leaves" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Leave & Gate Pass Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <p className="text-muted-foreground mb-3">
                  Leave requests are now managed through the unified Gate Pass system.
                  Students request leave, parents approve, then warden approves.
                </p>
                <a href="/gate-pass" className="inline-block">
                  <Button data-testid="go-to-gate-pass-admin-btn">
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Go to Gate Pass Management
                  </Button>
                </a>
              </div>
              
              {/* Show old leave requests for reference */}
              {leaveRequests.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-muted-foreground mb-3">Legacy Leave Requests (Read-only)</h4>
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-3 text-left text-sm font-medium">Student</th>
                        <th className="p-3 text-left text-sm font-medium">Room</th>
                        <th className="p-3 text-left text-sm font-medium">Dates</th>
                        <th className="p-3 text-left text-sm font-medium">Reason</th>
                        <th className="p-3 text-left text-sm font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveRequests.map(leave => (
                        <tr key={leave.id} className="border-t">
                          <td className="p-3 font-medium">{leave.student_name}</td>
                          <td className="p-3">{leave.room_number}</td>
                          <td className="p-3 text-sm">{leave.start_date} to {leave.end_date}</td>
                          <td className="p-3 text-sm">{leave.reason}</td>
                          <td className="p-3">
                            <Badge variant={leave.status === 'approved' ? 'default' : leave.status === 'rejected' ? 'destructive' : 'secondary'}>
                              {leave.status}
                              {leave.gate_pass_id && ' (Gate Pass Generated)'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visitors Tab */}
        <TabsContent value="visitors" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Visitor Management</h3>
            <Button onClick={() => setVisitorDialog(true)} data-testid="register-visitor-btn"><Plus className="h-4 w-4 mr-2" />Register Visitor</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium">Visitor</th>
                    <th className="p-3 text-left text-sm font-medium">Visiting</th>
                    <th className="p-3 text-left text-sm font-medium">Relation</th>
                    <th className="p-3 text-left text-sm font-medium">Purpose</th>
                    <th className="p-3 text-left text-sm font-medium">Check In</th>
                    <th className="p-3 text-left text-sm font-medium">Check Out</th>
                    <th className="p-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map(v => (
                    <tr key={v.id} className="border-t">
                      <td className="p-3">
                        <div><p className="font-medium">{v.visitor_name}</p><p className="text-xs text-muted-foreground">{v.visitor_phone}</p></div>
                      </td>
                      <td className="p-3">{v.student_name} ({v.room_number})</td>
                      <td className="p-3">{v.relation}</td>
                      <td className="p-3 text-sm">{v.purpose}</td>
                      <td className="p-3 text-sm">{v.check_in?.split('T')[1]?.slice(0,5) || '-'}</td>
                      <td className="p-3 text-sm">{v.check_out?.split('T')[1]?.slice(0,5) || '-'}</td>
                      <td className="p-3">
                        {v.status === 'checked_in' && (
                          <Button size="sm" onClick={() => checkoutVisitor(v.id)}>Checkout</Button>
                        )}
                        {v.status === 'checked_out' && <Badge>Completed</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" />Hostel Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Security Deposit */}
              <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                <h4 className="font-semibold text-blue-800">Security Deposit Configuration</h4>
                <p className="text-sm text-gray-600">Set how many months of room fee will be charged as security deposit (refundable on checkout)</p>
                <div className="flex items-center gap-4">
                  <Label>Security Deposit Months:</Label>
                  <Select 
                    value={String(hostelSettingsForm.security_deposit_months || 2)} 
                    onValueChange={v => setHostelSettingsForm({...hostelSettingsForm, security_deposit_months: parseInt(v)})}
                  >
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Month</SelectItem>
                      <SelectItem value="2">2 Months</SelectItem>
                      <SelectItem value="3">3 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-blue-600">
                  Current: {hostelSettingsForm.security_deposit_months || 2} month(s) of room fee
                </p>
              </div>

              {/* Other Settings */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Late Return Fine (₹)</Label>
                  <Input 
                    type="number" 
                    value={hostelSettingsForm.late_return_fine || 100} 
                    onChange={e => setHostelSettingsForm({...hostelSettingsForm, late_return_fine: parseFloat(e.target.value)})} 
                  />
                  <p className="text-xs text-muted-foreground mt-1">Fine per day for late return from leave</p>
                </div>
                <div>
                  <Label>Visitor Hours</Label>
                  <Input 
                    value={hostelSettingsForm.visitor_hours || '10:00 - 18:00'} 
                    onChange={e => setHostelSettingsForm({...hostelSettingsForm, visitor_hours: e.target.value})} 
                    placeholder="e.g., 10:00 - 18:00"
                  />
                </div>
                <div>
                  <Label>Curfew Time</Label>
                  <Input 
                    value={hostelSettingsForm.curfew_time || '22:00'} 
                    onChange={e => setHostelSettingsForm({...hostelSettingsForm, curfew_time: e.target.value})} 
                    placeholder="e.g., 22:00"
                  />
                </div>
              </div>

              <Button onClick={saveHostelSettings} data-testid="save-hostel-settings-btn">
                <Save className="h-4 w-4 mr-2" />Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Building Dialog */}
      <Dialog open={buildingDialog} onOpenChange={closeBuildingDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingBuilding ? 'Edit Building' : 'Add Hostel Building'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Building Name *</Label><Input value={buildingForm.name} onChange={e => setBuildingForm({...buildingForm, name: e.target.value})} placeholder="e.g., Boys Hostel A" /></div>
            <div><Label>Type</Label>
              <Select value={buildingForm.building_type} onValueChange={v => setBuildingForm({...buildingForm, building_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="boys">Boys</SelectItem>
                  <SelectItem value="girls">Girls</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Total Floors</Label><Input type="number" value={buildingForm.total_floors} onChange={e => setBuildingForm({...buildingForm, total_floors: parseInt(e.target.value)})} /></div>
            <div>
              <Label>Warden</Label>
              <Select 
                value={buildingForm.warden_id} 
                onValueChange={v => {
                  const selectedStaff = staffList.find(s => s.id === v);
                  setBuildingForm({
                    ...buildingForm, 
                    warden_id: v, 
                    warden_name: selectedStaff?.name || ''
                  });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select Warden" /></SelectTrigger>
                <SelectContent>
                  {staffList.map(staff => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name} {staff.designation ? `(${staff.designation})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {staffList.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">No staff found. Add staff first.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBuildingDialog}>Cancel</Button>
            <Button onClick={editingBuilding ? updateBuilding : createBuilding}>{editingBuilding ? 'Update' : 'Create'} Building</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={roomDialog} onOpenChange={closeRoomDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingRoom ? 'Edit Room' : 'Add Room'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Room Number *</Label><Input value={roomForm.room_number} onChange={e => setRoomForm({...roomForm, room_number: e.target.value})} placeholder="e.g., 101" /></div>
            <div><Label>Floor</Label><Input type="number" value={roomForm.floor} onChange={e => setRoomForm({...roomForm, floor: parseInt(e.target.value)})} /></div>
            <div><Label>Room Type</Label>
              <Select value={roomForm.room_type} onValueChange={v => setRoomForm({...roomForm, room_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  <SelectItem value="double">Double</SelectItem>
                  <SelectItem value="triple">Triple</SelectItem>
                  <SelectItem value="dormitory">Dormitory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Total Beds</Label><Input type="number" value={roomForm.total_beds} onChange={e => setRoomForm({...roomForm, total_beds: parseInt(e.target.value)})} /></div>
            <div><Label>Monthly Fee (₹)</Label><Input type="number" value={roomForm.monthly_fee} onChange={e => setRoomForm({...roomForm, monthly_fee: parseFloat(e.target.value)})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRoomDialog}>Cancel</Button>
            <Button onClick={editingRoom ? updateRoom : createRoom}>{editingRoom ? 'Update' : 'Create'} Room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Student Dialog */}
      <Dialog open={allocateDialog} onOpenChange={setAllocateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Allocate Student to Hostel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Student</Label>
              <Select value={allocateForm.student_id} onValueChange={v => setAllocateForm({...allocateForm, student_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {students.filter(s => !s.is_hosteller).map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.student_id})</SelectItem>)}
                  {students.filter(s => !s.is_hosteller).length === 0 && (
                    <div className="p-2 text-sm text-muted-foreground">No unallocated students</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Room</Label>
              <Select value={allocateForm.room_id} onValueChange={v => setAllocateForm({...allocateForm, room_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>
                  {rooms.filter(r => r.available_beds > 0).map(r => <SelectItem key={r.id} value={r.id}>{r.building_name} - Room {r.room_number} ({r.available_beds} beds)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Bed Number</Label><Input type="number" value={allocateForm.bed_number} onChange={e => setAllocateForm({...allocateForm, bed_number: parseInt(e.target.value)})} min={1} /></div>
              <div><Label>Start Date</Label><Input type="date" value={allocateForm.start_date} onChange={e => setAllocateForm({...allocateForm, start_date: e.target.value})} /></div>
            </div>
            <div><Label>Dietary Preference</Label>
              <Select value={allocateForm.mess_type} onValueChange={v => setAllocateForm({...allocateForm, mess_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="veg">Vegetarian</SelectItem>
                  <SelectItem value="non_veg">Non-Vegetarian</SelectItem>
                  <SelectItem value="special">Special Diet</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Mess is included in room package</p>
            </div>
          </div>
          <DialogFooter><Button onClick={allocateStudent}>Allocate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visitor Dialog */}
      <Dialog open={visitorDialog} onOpenChange={setVisitorDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Register Visitor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Visiting Student</Label>
              <Select value={visitorForm.student_id} onValueChange={v => setVisitorForm({...visitorForm, student_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>
                  {allocations.map(a => <SelectItem key={a.student_id} value={a.student_id}>{a.student_name} - Room {a.room_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Visitor Name *</Label><Input value={visitorForm.visitor_name} onChange={e => setVisitorForm({...visitorForm, visitor_name: e.target.value})} /></div>
            <div><Label>Visitor Phone *</Label><Input value={visitorForm.visitor_phone} onChange={e => setVisitorForm({...visitorForm, visitor_phone: e.target.value})} /></div>
            <div><Label>Relation *</Label><Input value={visitorForm.relation} onChange={e => setVisitorForm({...visitorForm, relation: e.target.value})} placeholder="e.g., Father, Uncle" /></div>
            <div><Label>Purpose</Label><Input value={visitorForm.purpose} onChange={e => setVisitorForm({...visitorForm, purpose: e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={registerVisitor}>Register</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Dialog */}
      <Dialog open={menuDialog} onOpenChange={closeMenuDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add/Update Menu</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Day</Label>
              <Select value={menuForm.day} onValueChange={v => setMenuForm({...menuForm, day: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{days.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Meal Type</Label>
              <Select value={menuForm.meal_type} onValueChange={v => setMenuForm({...menuForm, meal_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {mealTypes.map(m => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Items</Label><Textarea value={menuForm.items} onChange={e => setMenuForm({...menuForm, items: e.target.value})} placeholder="e.g., Rice, Dal, Sabzi, Roti" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMenuDialog}>Cancel</Button>
            <Button onClick={saveMessMenu}>Save Menu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mess Timing Settings Dialog - Fee fields removed since mess is included in room package */}
      <Dialog open={messSettingsDialog} onOpenChange={setMessSettingsDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mess Timing Settings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Note: Mess fee is now included in room package. Configure meal timings below.</p>
            <div><Label>Breakfast Time</Label><Input value={settingsForm.breakfast_time || ''} onChange={e => setSettingsForm({...settingsForm, breakfast_time: e.target.value})} placeholder="07:00 - 08:30" /></div>
            <div><Label>Lunch Time</Label><Input value={settingsForm.lunch_time || ''} onChange={e => setSettingsForm({...settingsForm, lunch_time: e.target.value})} placeholder="12:30 - 14:00" /></div>
            <div><Label>Snacks Time</Label><Input value={settingsForm.snacks_time || ''} onChange={e => setSettingsForm({...settingsForm, snacks_time: e.target.value})} placeholder="17:00 - 17:30" /></div>
            <div><Label>Dinner Time</Label><Input value={settingsForm.dinner_time || ''} onChange={e => setSettingsForm({...settingsForm, dinner_time: e.target.value})} placeholder="19:30 - 21:00" /></div>
          </div>
          <DialogFooter><Button onClick={saveMessSettings}>Save Timings</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// Main Component with Role-based Routing
const HostelPageWrapper = () => {
  const { getAuthHeader, user } = useAuth();
  
  // Student gets simplified view
  if (user?.role === 'student') {
    return <StudentHostelView getAuthHeader={getAuthHeader} />;
  }
  
  // Admin gets full view
  return <HostelPageV2Admin />;
};

export default HostelPageWrapper;
